import { Recipient } from '../../communication/model/recipient';

/**
 * Explicit per-recipient signing method. Single source of truth for
 * the segmented control, recipient-list badge/chips, and the prepare toolbar
 * filter. `aes` is reserved by the API contract but not selectable yet.
 */
export type SignMethodCode = 'qes' | 'bio' | 'initials';

/** Identity-auth group revealed for a method: T.C. Kimlik, SMS, or none. */
export type IdentityType = 'tck' | 'sms' | null;

export interface SignMethodMeta {
  code: SignMethodCode;
  /** ngx-translate key for the short segment/badge label. */
  labelKey: string;
  /** ngx-translate key for the one-line method description. */
  descriptionKey: string;
  /** Phosphor icon class. */
  icon: string;
  /** Accent colour. */
  accent: string;
  /** Accent tint (segment/badge background). */
  accentTint: string;
  /** Which identity-auth group this method drives. */
  identity: IdentityType;
  /**
   * The single signature-family widget the prepare toolbar allows for a
   * recipient on this method. Value matches form-designer `FormElementType`.
   */
  signatureWidget: 'signature' | 'biometric-signature' | 'initials';
  /** ngx-translate key for the DOĞRULAMA chip text, null when no identity. */
  identityChipKey: string | null;
}

export const SIGN_METHODS: Record<SignMethodCode, SignMethodMeta> = {
  qes: {
    code: 'qes',
    labelKey: 'signMethod.qes.label',
    descriptionKey: 'signMethod.qes.description',
    icon: 'ph-bold ph-pen-nib',
    accent: '#2f8def',
    accentTint: '#eaf3fe',
    identity: 'tck',
    signatureWidget: 'signature',
    identityChipKey: 'signMethod.identity.tck'
  },
  bio: {
    code: 'bio',
    labelKey: 'signMethod.bio.label',
    descriptionKey: 'signMethod.bio.description',
    icon: 'ph-bold ph-fingerprint',
    accent: '#12a884',
    accentTint: '#e6f7f2',
    identity: 'sms',
    signatureWidget: 'biometric-signature',
    identityChipKey: 'signMethod.identity.sms'
  },
  initials: {
    code: 'initials',
    labelKey: 'signMethod.initials.label',
    descriptionKey: 'signMethod.initials.description',
    icon: 'ph-bold ph-signature',
    accent: '#7257f0',
    accentTint: '#efeafe',
    identity: null,
    signatureWidget: 'initials',
    identityChipKey: null
  }
};

/** Stub method shown in the segmented control but not yet implemented. */
export const VIEWER_STUB = {
  code: 'viewer',
  labelKey: 'signMethod.viewer.label',
  icon: 'ph-bold ph-eye',
  accent: '#64748b',
  accentTint: '#eef1f5'
} as const;

export const SIGN_METHOD_ORDER: SignMethodCode[] = ['qes', 'bio', 'initials'];

export function isSignMethodCode(value: string | null | undefined): value is SignMethodCode {
  return value === 'qes' || value === 'bio' || value === 'initials';
}

export function getSignMethodMeta(code: string | null | undefined): SignMethodMeta | null {
  return isSignMethodCode(code) ? SIGN_METHODS[code] : null;
}

/**
 * Non-signature widgets available regardless of signing method, expressed as
 * form-designer `FormElementType` string values. Single source of truth shared
 * by `allowedWidgetsForMethod` and the prepare toolbar's unrestricted default
 * so the two allow-lists cannot drift apart.
 */
export const NON_SIGNATURE_WIDGETS: string[] = [
  'text',
  'text-input',
  'radio-button',
  'checkbox',
  'file-list'
];

/**
 * The non-signature widgets plus the one signature-family widget the method
 * permits. A legacy-null method yields `null` so the caller can fall back to
 * the unrestricted (all-widgets) default.
 */
export function allowedWidgetsForMethod(code: string | null | undefined): string[] | null {
  const meta = getSignMethodMeta(code);
  if (!meta) {
    return null;
  }
  return [meta.signatureWidget, ...NON_SIGNATURE_WIDGETS];
}

/** Maps the generated `Recipient.SignMethodEnum` (incl. reserved `aes`) to a selectable code, or null. */
export function normalizeSignMethod(value: Recipient.SignMethodEnum | string | null | undefined): SignMethodCode | null {
  return isSignMethodCode(value) ? value : null;
}

/**
 * Finish-time guard: returns every recipient whose method is `bio`
 * or `initials` but has no placed signature widget of the required type. Returns
 * an empty array when every such recipient is satisfied. QES and legacy-null
 * recipients are exempt. `elements` are form-designer elements (each with
 * `signerId` and `type`); a recipient's signer id is its stringified `id`,
 * matching how fields map back to signers in prepare / prepare-self.
 */
export function findRecipientsMissingSignatureWidget<T extends { id?: any; signMethod?: any }>(
  recipients: T[] | null | undefined,
  elements: Array<{ signerId?: any; type?: any }> | null | undefined
): T[] {
  const placed = elements || [];
  const missing: T[] = [];
  for (const recipient of recipients || []) {
    const meta = getSignMethodMeta(recipient.signMethod);
    if (!meta || (meta.code !== 'bio' && meta.code !== 'initials')) {
      continue;
    }
    const signerId = recipient.id != null ? recipient.id.toString() : undefined;
    const hasWidget = placed.some(el => el.signerId === signerId && el.type === meta.signatureWidget);
    if (!hasWidget) {
      missing.push(recipient);
    }
  }
  return missing;
}
